import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  Clock,
  Calendar,
  Target,
  User,
  DollarSign,
  FileText,
  CheckCircle,
  AlertTriangle,
  Plus,
  X,
  ChevronRight,
  ChevronLeft,
  Loader2
} from 'lucide-react';
import { useToast } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import FormStepper from '../common/FormStepper';

// ────────────────────────────────────────────────────────────────────
// STEP DEFINITIONS
// ────────────────────────────────────────────────────────────────────
const STEPS = [
  { id: 0, label: 'Time Details', icon: Clock, hint: 'Project, task, and time information' },
  { id: 1, label: 'Billing Info', icon: DollarSign, hint: 'Rate and billing details' },
  { id: 2, label: 'Additional', icon: FileText, hint: 'Tags, notes, and attachments' },
];

const CreateTimeEntry = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  
  // Step management
  const [currentStep, setCurrentStep] = useState(0);
  
  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    project: '',
    task: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    duration: '',
    billable: true,
    hourlyRate: '',
    totalAmount: '',
    
    // Additional Details
    category: '',
    tags: [],
    notes: '',
    attachments: [],
    
    // Status
    status: 'pending',
    approvedBy: '',
    approvedAt: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);

  // Step navigation handlers
  const handleNext = (e) => {
    if (e) e.preventDefault();
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(s => s + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(s => s - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleStepClick = (idx) => {
    if (idx <= currentStep) setCurrentStep(idx);
  };

  // Mock data for fallback
  const mockProjects = [
    { id: 1, name: 'Digital Transformation Initiative' },
    { id: 2, name: 'Infrastructure Modernization' },
    { id: 3, name: 'Customer Experience Enhancement' },
    { id: 4, name: 'Data Analytics Platform' },
    { id: 5, name: 'Mobile App Development' }
  ];

  const mockTasks = [
    { id: 1, title: 'Implement user authentication system', projectId: 1 },
    { id: 2, title: 'Database optimization', projectId: 2 },
    { id: 3, title: 'UI/UX improvements', projectId: 3 },
    { id: 4, title: 'API development', projectId: 1 },
    { id: 5, title: 'Testing and QA', projectId: 4 }
  ];

  // Load projects on component mount
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const response = await fetch(`${API_CONFIG.ENDPOINTS.PROJECTS}?limit=1000`, {
          headers: API_CONFIG.HEADERS
        });
        if (response.ok) {
          const data = await response.json();
          const projectsList = data.data?.projects || data.projects || data.data || [];
          setProjects(projectsList.map(p => ({
            id: p._id || p.id,
            name: p.name || p.projectName || 'Unnamed Project'
          })));
        } else {
          // Fallback to mock data if API fails
          setProjects(mockProjects);
        }
      } catch (err) {
        console.warn('Failed to load projects, using mock data:', err);
        setProjects(mockProjects);
      }
    };

    loadProjects();
  }, []);

  // Load tasks when project is selected
  useEffect(() => {
    const loadTasks = async () => {
      if (!formData.project) {
        setTasks([]);
        return;
      }

      try {
        const response = await fetch(`${API_CONFIG.ENDPOINTS.TASKS}?project=${formData.project}&limit=1000`, {
          headers: API_CONFIG.HEADERS
        });
        if (response.ok) {
          const data = await response.json();
          const tasksList = data.data?.tasks || data.tasks || data.data || [];
          setTasks(tasksList.map(t => ({
            id: t._id || t.id,
            title: t.title || 'Unnamed Task',
            projectId: t.project?._id || t.project || formData.project
          })));
        } else {
          // Fallback to mock data if API fails
          setTasks(mockTasks.filter(t => t.projectId === parseInt(formData.project)));
        }
      } catch (err) {
        console.warn('Failed to load tasks, using mock data:', err);
        setTasks(mockTasks.filter(t => t.projectId === parseInt(formData.project)));
      }
    };

    loadTasks();
  }, [formData.project]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-calculate duration and amount
    if (field === 'startTime' || field === 'endTime') {
      calculateDuration();
    }
    if (field === 'duration' || field === 'hourlyRate') {
      calculateAmount();
    }
  };

  // Calculate duration from start and end time
  const calculateDuration = () => {
    if (formData.startTime && formData.endTime) {
      const start = new Date(`2000-01-01T${formData.startTime}`);
      const end = new Date(`2000-01-01T${formData.endTime}`);
      const diffMs = end - start;
      const diffHours = diffMs / (1000 * 60 * 60);
      
      if (diffHours > 0) {
        setFormData(prev => ({
          ...prev,
          duration: diffHours.toFixed(2)
        }));
      }
    }
  };

  // Calculate total amount
  const calculateAmount = () => {
    const duration = parseFloat(formData.duration) || 0;
    const rate = parseFloat(formData.hourlyRate) || 0;
    const amount = duration * rate;
    
    setFormData(prev => ({
      ...prev,
      totalAmount: amount.toFixed(2)
    }));
  };

  // Handle project change
  const handleProjectChange = (projectId) => {
    setFormData(prev => ({
      ...prev,
      project: projectId,
      task: '' // Reset task when project changes
    }));
  };

  // Handle tag addition
  const handleAddTag = (tag) => {
    if (tag.trim() && !formData.tags.includes(tag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag.trim()]
      }));
    }
  };

  // Handle tag removal
  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
    const payload = {
      project: formData.project, // MUST be ObjectId
      task: formData.task || null,
      description: formData.description,
      date: formData.date,
        startTime: formData.startTime,
      endTime: formData.endTime,
      duration: Number(formData.duration),
      billable: formData.billable,
      hourlyRate: Number(formData.hourlyRate || 0),
      status: formData.status,
      notes: formData.notes
    };

      const response = await fetch(
        API_CONFIG.ENDPOINTS.TIME_ENTRIES,
        {
          method: 'POST',
          headers: {
            ...API_CONFIG.HEADERS,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            project: formData.project,
            task: formData.task || undefined,
            description: formData.description,
            date: formData.date,
            startTime: formData.startTime,
            endTime: formData.endTime || undefined,
            duration: parseFloat(formData.duration) || 0,
            billable: formData.billable,
            hourlyRate: parseFloat(formData.hourlyRate) || 0,
            totalAmount: parseFloat(formData.totalAmount) || 0,
            status: formData.status,
            notes: formData.notes || undefined,
            tags: formData.tags.length > 0 ? formData.tags : undefined
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create time entry');
      }

      const result = await response.json();
      if (result.success) {
        success('Time entry created successfully!');
        navigate('/projects/time-tracking/entry');
      } else {
        throw new Error(result.message || 'Failed to create time entry');
      }
    } catch (err) {
    console.error(err);
    showError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get filtered tasks based on selected project
  const getFilteredTasks = () => {
    if (!formData.project) return tasks;

  return tasks.filter(task => {
    // If project is populated object
    if (task.project && typeof task.project === 'object') {
      return task.project._id === formData.project;
    }

    // If project is ObjectId string
    return task.project === formData.project;
  });
  };

  // ─── Step Content Renderers ───────────────────────────────────

  // STEP 0: Time Details
  const renderStep0 = () => (
    <div className="project-form-section">
      <h4 className="mb-4">Time Entry Details</h4>
      <p className="text-muted mb-4">Basic information about your time entry</p>
      
      {/* 4-Column Grid Layout */}
      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Project *</label>
          <select
            className="form-select"
            value={formData.project}
            onChange={(e) => handleProjectChange(e.target.value)}
            required
          >
            <option value="">Select Project</option>
            {projects.map(project => (
              <option key={project._id} value={project._id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Task</label>
          <select
            className="form-select"
            value={formData.task}
            disabled={!formData.project}
            onChange={(e) => handleInputChange('task', e.target.value)}
          >
            <option value="">Select Task</option>
            {getFilteredTasks().map(task => (
              <option key={task._id} value={task._id}>
                {task.title}
              </option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Date *</label>
          <input
            type="date"
            className="form-control"
            value={formData.date}
            onChange={(e) => handleInputChange('date', e.target.value)}
            required
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Category</label>
          <select
            className="form-select"
            value={formData.category}
            onChange={(e) => handleInputChange('category', e.target.value)}
          >
            <option value="">Select Category</option>
            <option value="development">Development</option>
            <option value="design">Design</option>
            <option value="testing">Testing</option>
            <option value="meeting">Meeting</option>
            <option value="research">Research</option>
            <option value="documentation">Documentation</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      {/* Time Information */}
      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Start Time</label>
          <input
            type="time"
            className="form-control"
            value={formData.startTime}
            onChange={(e) => handleInputChange('startTime', e.target.value)}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">End Time</label>
          <input
            type="time"
            className="form-control"
            value={formData.endTime}
            onChange={(e) => handleInputChange('endTime', e.target.value)}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Duration (hours) *</label>
          <input
            type="number"
            className="form-control"
            value={formData.duration}
            onChange={(e) => handleInputChange('duration', e.target.value)}
            placeholder="0.00"
            step="0.25"
            min="0"
            required
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Status</label>
          <select
            className="form-select"
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Description */}
      <div className="form-group">
        <label className="form-label">Description *</label>
        <textarea
          className="form-control"
          rows="3"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Describe what you worked on..."
          required
        />
      </div>
    </div>
  );

  // STEP 1: Billing Information
  const renderStep1 = () => (
    <div className="project-form-section">
      <h4 className="mb-4">Billing Information</h4>
      <p className="text-muted mb-4">Billing details and rate information</p>
      
      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Billable</label>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="billable"
              checked={formData.billable}
              onChange={(e) => handleInputChange('billable', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="billable">
              This time entry is billable
            </label>
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Hourly Rate ($)</label>
          <input
            type="number"
            className="form-control"
            value={formData.hourlyRate}
            onChange={(e) => handleInputChange('hourlyRate', e.target.value)}
            placeholder="0.00"
            step="0.01"
            min="0"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Total Amount ($)</label>
          <input
            type="number"
            className="form-control"
            value={formData.totalAmount}
            readOnly
            style={{ backgroundColor: '#f8f9fa' }}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Currency</label>
          <select
            className="form-select"
            value="USD"
            disabled
          >
            <option value="USD">USD</option>
          </select>
        </div>
      </div>
    </div>
  );

  // STEP 2: Additional Information
  const renderStep2 = () => (
    <div className="project-form-section">
      <h4 className="mb-4">Additional Information</h4>
      <p className="text-muted mb-4">Tags, notes, and other details</p>
      
      {/* Tags */}
      <div className="form-group">
        <label className="form-label">Tags</label>
        <div className="tags-container">
          <div className="tags-input">
            <input
              type="text"
              className="form-control"
              placeholder="Add a tag and press Enter"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag(e.target.value);
                  e.target.value = '';
                }
              }}
            />
          </div>
          <div className="tags-list">
            {formData.tags.map((tag, index) => (
              <span key={index} className="tag">
                {tag}
                <button
                  type="button"
                  className="tag-remove"
                  onClick={() => handleRemoveTag(tag)}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="form-group">
        <label className="form-label">Notes</label>
        <textarea
          className="form-control"
          rows="3"
          value={formData.notes}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          placeholder="Additional notes or comments..."
        />
      </div>
    </div>
  );

  const stepRenderers = [renderStep0, renderStep1, renderStep2];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Create Time Entry"
        description="Log your time spent on projects and tasks with detailed tracking"
      />
      <div className="page-container">

      {/* Main Content */}
      <div className="project-create-container">
        <form onSubmit={currentStep === STEPS.length - 1 ? handleSubmit : handleNext}>
          {/* Multi-step Header */}
          <FormStepper
            steps={STEPS}
            currentStep={currentStep}
            onStepClick={handleStepClick}
          />

          {/* Step Content */}
          <div className="project-form-card">
            {/* Animated Current Step */}
            <div key={currentStep} style={{ animation: 'fadeIn 0.25s ease-in' }}>
              {stepRenderers[currentStep]()}
            </div>
          </div>

          {/* Navigation Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '32px 0 0 0',
            borderTop: '1px solid var(--border-color)',
            marginTop: '40px'
          }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={() => navigate('/projects/time-tracking/entry')}
                className="btn btn-secondary"
                style={{ minWidth: '100px', borderRadius: '0', padding: '12px 24px' }}
              >
                <X size={16} />
                Cancel
              </button>
              {currentStep > 0 && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="btn btn-secondary"
                  style={{
                    minWidth: '100px',
                    borderRadius: '0',
                    padding: '12px 24px',
                    border: '1px solid var(--gold-primary)',
                    color: 'var(--gold-primary)'
                  }}
                >
                  <ChevronLeft size={16} />
                  Back
                </button>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              {/* Step dots */}
              <div style={{ display: 'flex', gap: '6px' }}>
                {STEPS.map((_, idx) => (
                  <div
                    key={idx}
                    style={{
                      width: idx === currentStep ? '20px' : '8px',
                      height: '8px',
                      borderRadius: '4px',
                      background: idx < currentStep ? 'var(--gold-bright)' : idx === currentStep ? 'var(--gold-primary)' : 'var(--divider-color)',
                      transition: 'all 0.3s ease',
                    }}
                  />
                ))}
              </div>

              {currentStep < STEPS.length - 1 ? (
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{
                    minWidth: '130px',
                    borderRadius: '0',
                    padding: '12px 28px',
                    backgroundColor: 'var(--gold-primary)',
                    border: 'none'
                  }}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn btn-primary"
                  style={{
                    minWidth: '160px',
                    borderRadius: '0',
                    padding: '12px 28px',
                    backgroundColor: isSubmitting ? 'var(--text-muted)' : 'var(--gold-primary)',
                    border: 'none'
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Create Time Entry
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
      </div>
    </>
  );
};

export default CreateTimeEntry;

// Global Fade In Animation
const styleSheet = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = styleSheet;
  if (!document.head.querySelector('style[data-time-entry-create-styles]')) {
    style.setAttribute('data-time-entry-create-styles', 'true');
    document.head.appendChild(style);
  }
}